import { supabase, getActiveOrgOverride } from '../supabaseClient'
import { STARTER_DEFAULTS } from '../entitlements'

// Sin subscription (o sin limites en el plan) = tier gratis Starter, igual que la UI
// (computeEntitlements). Antes se trataba como 0/bloqueado, y el gate real chocaba con
// lo que la UI mostraba: la org "podia" crear pero createPlayer cortaba con PLAN_LIMIT.
const RESOURCE_TO_LIMIT_KEY = {
  players: 'players_max',
  groups: 'groups_max',
  staff: 'staff_max',
  deportes: 'deportes_max',
}

export const getCurrentSubscription = async (orgId) => {
  if (!orgId) {
    return { data: null, error: { message: 'orgId required' } }
  }

  const { data, error } = await supabase
    .from('subscriptions')
    .select('*, plan:subscription_plans(*)')
    .eq('org_id', orgId)
    .limit(1)
    .maybeSingle()

  return { data, error }
}

export const getCurrentPlan = async (orgId) => {
  const { data, error } = await getCurrentSubscription(orgId)
  if (error || !data || !data.plan) return 'starter'
  return data.plan.code
}

export const getPlanLimits = async (orgId) => {
  const { data, error } = await getCurrentSubscription(orgId)
  if (error || !data || !data.plan || !data.plan.limits) {
    return { ...STARTER_DEFAULTS.limits }
  }
  return data.plan.limits
}

export const hasFeature = async (orgId, featureKey) => {
  const { data, error } = await getCurrentSubscription(orgId)
  if (error || !data || !data.plan || !data.plan.features) return false
  return data.plan.features[featureKey] === true
}

export const getCurrentUsage = async (orgId) => {
  if (!orgId) {
    return { data: null, error: { message: 'orgId required' } }
  }

  const { data, error } = await supabase.rpc('count_org_resource_usage', {
    p_org_id: orgId,
  })

  if (error) return { data: null, error }

  const row = Array.isArray(data) ? data[0] : null
  if (!row) {
    return {
      data: { players: 0, groups: 0, staff: 0, deportes: 0 },
      error: null,
    }
  }

  return {
    data: {
      players: row.players_count,
      groups: row.groups_count,
      staff: row.staff_count,
      deportes: row.deportes_count,
    },
    error: null,
  }
}

export const canCreate = async (orgId, resource) => {
  const { data: sub } = await getCurrentSubscription(orgId)
  // Sin subscription = tier gratis Starter (no bloqueado). Grandfathered = ilimitado.
  if (sub?.grandfathered_forever) return true
  if (sub?.status === 'read_only' || sub?.status === 'paused') return false

  const limitKey = RESOURCE_TO_LIMIT_KEY[resource]
  if (!limitKey) return true

  const limits = sub?.plan?.limits || STARTER_DEFAULTS.limits
  const limit = limits?.[limitKey]
  if (limit === undefined || limit === null) return false

  const { data: usage } = await getCurrentUsage(orgId)
  if (!usage) return false

  return (usage[resource] ?? 0) < limit
}

export const isReadOnly = async (orgId) => {
  const { data: sub } = await getCurrentSubscription(orgId)
  if (!sub) return false
  return sub.status === 'read_only' || sub.status === 'paused'
}

// Cupo disponible de un recurso segun el plan. Devuelve:
// { readOnly, unlimited, limit, used, remaining }.
export const getRemainingCapacity = async (orgId, resource) => {
  const { data: sub } = await getCurrentSubscription(orgId)

  if (sub?.status === 'read_only' || sub?.status === 'paused') {
    return { readOnly: true, unlimited: false, limit: 0, used: 0, remaining: 0 }
  }

  const { data: usage } = await getCurrentUsage(orgId)
  const used = usage?.[resource] ?? 0

  if (sub?.grandfathered_forever) {
    return { readOnly: false, unlimited: true, limit: Infinity, used, remaining: Infinity }
  }

  const limitKey = RESOURCE_TO_LIMIT_KEY[resource]
  const limits = sub?.plan?.limits || STARTER_DEFAULTS.limits
  const limit = limitKey ? limits?.[limitKey] : undefined
  if (limit === undefined || limit === null) {
    return { readOnly: false, unlimited: false, limit: 0, used, remaining: 0 }
  }
  return { readOnly: false, unlimited: false, limit, used, remaining: Math.max(0, limit - used) }
}

// ---------------------------------------------------------------------------
// Platform admin: gestion manual de planes (sin Stripe)
// ---------------------------------------------------------------------------

export const getAllSubscriptionPlans = async () => {
  return await supabase
    .from('subscription_plans')
    .select('id, code, name, description, tier, active, limits, features, app_key')
    .eq('active', true)
    .order('tier', { ascending: true })
}

export const getAllSubscriptionPlansWithPrices = async () => {
  return await supabase
    .from('subscription_plans')
    .select(`
      id, code, name, description, tier, active, limits, features, app_key,
      prices:subscription_plan_prices (
        id, currency, billing_cycle, amount, active
      )
    `)
    .eq('active', true)
    .order('tier', { ascending: true })
}

// ---------------------------------------------------------------------------
// Solicitudes de cambio de plan (pre-Stripe, manual approval)
// ---------------------------------------------------------------------------

const PLAN_REQUEST_SELECT = `
  id, org_id, requested_by_staff_id, from_plan_id, to_plan_id, status,
  note, resolution_note, requested_at, resolved_at, resolved_by_staff_id, updated_at,
  org:organizations (id, name, short_name, slug, logo_url),
  from_plan:subscription_plans!from_plan_id (id, code, name),
  to_plan:subscription_plans!to_plan_id (id, code, name),
  requested_by:staff!requested_by_staff_id (id, nombre_completo, email)
`

export const getPendingRequestForOrg = async (orgId) => {
  if (!orgId) return { data: null, error: { message: 'orgId required' } }
  return await supabase
    .from('plan_change_requests')
    .select(PLAN_REQUEST_SELECT)
    .eq('org_id', orgId)
    .eq('status', 'pending')
    .maybeSingle()
}

export const createPlanChangeRequest = async ({ orgId, toPlanCode, staffId, note }) => {
  if (!orgId || !toPlanCode || !staffId) {
    return { data: null, error: { message: 'orgId, toPlanCode y staffId son requeridos' } }
  }

  // Resolver org internamente: el override de impersonacion gana. Asi la
  // solicitud no depende de que el caller haya resuelto la org activa.
  const effectiveOrgId = getActiveOrgOverride() || orgId

  const { data: toPlan, error: toErr } = await supabase
    .from('subscription_plans')
    .select('id')
    .eq('code', toPlanCode)
    .maybeSingle()
  if (toErr || !toPlan) return { data: null, error: toErr || { message: 'Plan destino no existe' } }

  const { data: currentSub } = await supabase
    .from('subscriptions')
    .select('plan_id')
    .eq('org_id', effectiveOrgId)
    .maybeSingle()

  return await supabase
    .from('plan_change_requests')
    .insert({
      org_id: effectiveOrgId,
      requested_by_staff_id: staffId,
      from_plan_id: currentSub?.plan_id || null,
      to_plan_id: toPlan.id,
      note: note?.trim() || null,
      status: 'pending',
    })
    .select(PLAN_REQUEST_SELECT)
    .single()
}

export const cancelPlanChangeRequest = async (requestId) => {
  return await supabase
    .from('plan_change_requests')
    .update({ status: 'canceled', resolved_at: new Date().toISOString() })
    .eq('id', requestId)
    .eq('status', 'pending')
    .select(PLAN_REQUEST_SELECT)
    .single()
}

export const getAllPendingPlanRequests = async () => {
  return await supabase
    .from('plan_change_requests')
    .select(PLAN_REQUEST_SELECT)
    .eq('status', 'pending')
    .order('requested_at', { ascending: true })
}

// Lista de solicitudes con filtro de status (platform_admin).
// statuses: array de strings ('pending'|'approved'|'rejected'|'canceled') o null = todas.
// limit: cuantas traer. Pendientes se ordenan ascendente (mas viejas arriba);
// resueltas se ordenan descendente (mas recientes arriba).
export const getAllPlanRequests = async ({ statuses = null, limit = 50 } = {}) => {
  let query = supabase
    .from('plan_change_requests')
    .select(PLAN_REQUEST_SELECT)
    .limit(limit)

  if (statuses && statuses.length > 0) {
    query = query.in('status', statuses)
  }

  // Si solo se piden pendientes, ordenar ASC (FIFO). Si no, DESC (mas recientes primero).
  const onlyPending = statuses && statuses.length === 1 && statuses[0] === 'pending'
  query = onlyPending
    ? query.order('requested_at', { ascending: true })
    : query.order('updated_at', { ascending: false })

  return await query
}

export const approvePlanChangeRequest = async ({ requestId, resolvedByStaffId, resolutionNote }) => {
  if (!requestId || !resolvedByStaffId) {
    return { data: null, error: { message: 'requestId y resolvedByStaffId requeridos' } }
  }

  const { data: req, error: reqErr } = await supabase
    .from('plan_change_requests')
    .select('id, org_id, to_plan:subscription_plans!to_plan_id (code)')
    .eq('id', requestId)
    .eq('status', 'pending')
    .maybeSingle()
  if (reqErr || !req) return { data: null, error: reqErr || { message: 'Solicitud no encontrada o ya resuelta' } }

  // Aplicar el cambio de plan. Mantenemos grandfathered_forever segun el estado actual,
  // pero el platform admin puede ajustarlo manualmente despues si lo necesita.
  const { data: currentSub } = await supabase
    .from('subscriptions')
    .select('grandfathered_forever, grandfathered_reason')
    .eq('org_id', req.org_id)
    .maybeSingle()

  const { error: planErr } = await setOrgSubscription(req.org_id, {
    planCode: req.to_plan.code,
    grandfathered: currentSub?.grandfathered_forever === true,
    reason: currentSub?.grandfathered_reason || null,
  })
  if (planErr) return { data: null, error: planErr }

  return await supabase
    .from('plan_change_requests')
    .update({
      status: 'approved',
      resolved_at: new Date().toISOString(),
      resolved_by_staff_id: resolvedByStaffId,
      resolution_note: resolutionNote?.trim() || null,
    })
    .eq('id', requestId)
    .select(PLAN_REQUEST_SELECT)
    .single()
}

export const rejectPlanChangeRequest = async ({ requestId, resolvedByStaffId, resolutionNote }) => {
  if (!requestId || !resolvedByStaffId) {
    return { data: null, error: { message: 'requestId y resolvedByStaffId requeridos' } }
  }
  return await supabase
    .from('plan_change_requests')
    .update({
      status: 'rejected',
      resolved_at: new Date().toISOString(),
      resolved_by_staff_id: resolvedByStaffId,
      resolution_note: resolutionNote?.trim() || null,
    })
    .eq('id', requestId)
    .eq('status', 'pending')
    .select(PLAN_REQUEST_SELECT)
    .single()
}

// Cambia el plan de una org. Si la org no tiene subscription, crea una.
// options: { planCode, grandfathered, reason }
//   - planCode: 'starter' | 'club' | 'pro'
//   - grandfathered: boolean — true = no cobrar, false = plan estandar
//   - reason: texto libre opcional (se guarda en grandfathered_reason)
// Requiere que el usuario sea platform_admin (enforced por RLS).
export const setOrgSubscription = async (orgId, options) => {
  if (!orgId) return { data: null, error: { message: 'orgId required' } }
  if (!options || !options.planCode) {
    return { data: null, error: { message: 'planCode required' } }
  }

  const { data: plan, error: planError } = await supabase
    .from('subscription_plans')
    .select('id')
    .eq('code', options.planCode)
    .maybeSingle()

  if (planError || !plan) {
    return { data: null, error: planError || { message: `Plan ${options.planCode} no existe` } }
  }

  const { data: existing } = await supabase
    .from('subscriptions')
    .select('id')
    .eq('org_id', orgId)
    .maybeSingle()

  const payload = {
    plan_id: plan.id,
    status: 'active',
    grandfathered_forever: options.grandfathered === true,
    grandfathered_at: options.grandfathered === true ? new Date().toISOString() : null,
    grandfathered_reason: options.reason || null,
  }

  if (existing) {
    return await supabase
      .from('subscriptions')
      .update(payload)
      .eq('id', existing.id)
      .select('*, plan:subscription_plans(*)')
      .single()
  }

  return await supabase
    .from('subscriptions')
    .insert({ org_id: orgId, ...payload })
    .select('*, plan:subscription_plans(*)')
    .single()
}
